import React from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography
} from "@mui/material";
import AdminData from "../../backend/dataservices/AdminData";

/**
 * Verwaltet die Ziel-Konten eines Hub-Users (Kontoverbund).
 *
 * Sobald mindestens ein Ziel-Konto gesetzt ist, kann der Hub-User nicht mehr
 * unter seinem eigenen Konto arbeiten, sondern muss nach dem Login eines der
 * Ziel-Konten uebernehmen.
 */
function AdminUserRelationsDialog({ user, allUsers, onClose, onSaved }) {
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [selected, setSelected] = React.useState([]);

    React.useEffect(() => {
        AdminData.getUserRelations(user.U_ID).then(rows => {
            setSelected(Array.isArray(rows) ? rows : []);
            setLoading(false);
        }).catch(() => {
            setError('Die Relationen konnten nicht geladen werden.');
            setLoading(false);
        });
    }, [user.U_ID]);

    // Als Ziel scheiden aus: das Konto selbst, Admin-Konten (wuerden dem
    // Hub-User Admin-Rechte verschaffen) und Konten, die selbst Relationen
    // haben (Ketten sind nicht erlaubt). Das Backend lehnt beides ebenfalls ab.
    const options = React.useMemo(() => allUsers.filter(u =>
        u.U_ID !== user.U_ID &&
        !u.U_IsAdmin &&
        !u.U_RelationCount
    ), [allUsers, user.U_ID]);

    const isTargetItself = !!user.U_RelationTargetCount;

    function optionLabel(u) {
        const name = u.U_FullName || u.U_Benutzername;
        return u.U_TenantCode ? `${name} (${u.U_TenantCode})` : name;
    }

    function handleSave() {
        setSaving(true);
        setError(null);
        AdminData.updateUserRelations(user.U_ID, selected.map(u => u.U_ID)).then(() => {
            onSaved(selected.length);
            onClose();
        }).catch(err => {
            setError(err?.response?.data?.error || 'Fehler beim Speichern.');
            setSaving(false);
        });
    }

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>User-Relationen</DialogTitle>
            <DialogContent>
                <Box sx={{pt: 1, display: 'flex', flexDirection: 'column', gap: 2}}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>{user.U_FullName || user.U_Benutzername}</strong>
                        {user.U_TenantCode ? ` · ${user.U_TenantCode}` : ''}
                    </Typography>

                    {loading ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', my: 3}}>
                            <CircularProgress/>
                        </Box>
                    ) : isTargetItself ? (
                        <Alert severity="warning">
                            Dieses Konto ist bereits Ziel einer anderen Relation und kann
                            deshalb selbst keine Relationen haben.
                        </Alert>
                    ) : (
                        <>
                            <Alert severity={selected.length > 0 ? "warning" : "info"}>
                                {selected.length > 0
                                    ? 'Dieser Benutzer kann nicht mehr unter seinem eigenen Konto arbeiten. Nach dem Login muss er eines der unten gewählten Konten übernehmen.'
                                    : 'Ohne Relationen arbeitet der Benutzer normal unter seinem eigenen Konto. Sobald Sie mindestens ein Konto zuweisen, wird er nach dem Login zur Kontoauswahl gezwungen.'}
                            </Alert>

                            <Autocomplete
                                multiple
                                options={options}
                                value={selected}
                                loading={loading}
                                onChange={(_, value) => setSelected(value)}
                                isOptionEqualToValue={(a, b) => a.U_ID === b.U_ID}
                                getOptionLabel={optionLabel}
                                renderOption={(props, u) => (
                                    <li {...props} key={u.U_ID}>
                                        <Box>
                                            <Typography variant="body2">
                                                {u.U_FullName || u.U_Benutzername}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {u.U_Benutzername}
                                                {' · '}
                                                {u.U_TenantCode || 'Kein Tenant'}
                                                {' · '}
                                                {u.U_Rolle || 'Keine Rolle'}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((u, index) => (
                                        <Chip
                                            size="small"
                                            label={optionLabel(u)}
                                            {...getTagProps({index})}
                                            key={u.U_ID}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Zugewiesene Konten"
                                        placeholder="Konto suchen …"
                                        size="small"
                                    />
                                )}
                            />

                            <Typography variant="caption" color="text.secondary">
                                Administrator-Konten und Konten, die selbst Relationen haben,
                                stehen nicht zur Auswahl.
                            </Typography>
                        </>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} style={{color: 'gray'}}>Abbrechen</Button>
                <Button onClick={handleSave} color="primary" disabled={loading || saving || isTargetItself}>
                    {saving ? <CircularProgress size={20}/> : 'Speichern'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AdminUserRelationsDialog;
